import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, Plus, RefreshCw, Trash2, UserPlus, X, Fingerprint } from "lucide-react";
import {
  useGetFirmEligibilityQuery,
  useGetFirmCurrentQuery,
  useFirmStep1Mutation,
  useFirmStep5Mutation,
  useFirmStep6Mutation,
  useFirmStep7Mutation,
  useFirmAcknowledgeMutation,
  useInitiateFirmPaymentMutation,
  useUploadFirmDocumentMutation,
} from "../api/firmApi";
import MemberSearchModal from "../components/MemberSearchModal";
import { firmStorageUrl, redirectToOnePay } from "../utils";
import { describeApiError } from "../../../lib/apiError";
import { FIRM_OWNERSHIP_STRUCTURES, BENEVOLENCE_TYPES } from "../../upgrade-application/constants";

/* The wizard's four screens, and the server `completion_step` each one leaves
   behind. The two numberings diverged when the ownership structure and the
   roster were folded into step 1 and the documents step was dropped — the
   server's endpoint numbers stayed put so existing drafts keep their meaning,
   and this table is the ONLY place the mapping lives. The last screen has no
   number of its own: it acknowledges (9) and pays in one go. */
const STEPS: { label: string; completes: number | null }[] = [
  { label: "Firm Details", completes: 4 },
  { label: "Benevolence", completes: 5 },
  { label: "Referees", completes: 6 },
  { label: "Chapter", completes: 7 },
  { label: "Declaration & Payment", completes: null },
];
const TOTAL_STEPS = STEPS.length;

/** The screen to resume on: the first whose work the server hasn't recorded. */
function resumeStep(completed: number): number {
  const i = STEPS.findIndex((s) => s.completes === null || s.completes > completed);
  return i < 0 ? TOTAL_STEPS : i + 1;
}

const inp = "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200";
const lbl = "block text-sm font-medium text-gray-700 mb-1";

/* The registration documents captured on step 1 — the only documents a firm
   application carries now that the free-form documents step is gone. */
const REG_DOC_TYPES = ["CAC Certificate", "Status Report", "Memorandum and Articles of Association"] as const;
const REG_DOCS: { type: string; label: string }[] = [
  { type: "CAC Certificate", label: "Certificate of registration issued by the Corporate Affairs Commission" },
  { type: "Status Report", label: "Status report" },
  { type: "Memorandum and Articles of Association", label: "Memorandum and Articles of Association (MEMART)" },
];

/* The forms a practice may be registered in. `FIRM_OWNERSHIP_STRUCTURES` is
   shared with the upgrade module and must match
   FirmApplicationController::OWNERSHIP_STRUCTURES exactly; the four company
   forms are grouped under one heading in the select. */
const SOLE_PROPRIETORSHIP = "Sole Proprietorship";
const COMPANY_FORMS = FIRM_OWNERSHIP_STRUCTURES.filter((s) => s.includes("Company"));
const OWNERSHIP_GROUPS: { group: string | null; options: string[] }[] = [
  { group: null, options: FIRM_OWNERSHIP_STRUCTURES.filter((s) => !COMPANY_FORMS.includes(s)) },
  { group: "Company", options: COMPANY_FORMS },
];

/* Everyone on the practice is one of these. Values must match
   FirmApplicationController::PERSON_ROLES. */
const PERSON_ROLES: { value: string; label: string }[] = [
  { value: "principal", label: "Principal Partner" },
  { value: "partner", label: "Partner" },
  { value: "director", label: "Director" },
];
const roleLabel = (role: string) => PERSON_ROLES.find((r) => r.value === role)?.label ?? "Person";

/* The C.V. block each person fills in (PDF 2.1 a–j, less the NIQS election
   date — a partner or director need not be a member). */
const PERSON_FIELDS: { key: string; label: string; type?: string; wide?: boolean }[] = [
  { key: "full_name", label: "Full name *" },
  { key: "previous_name", label: "Previous / maiden name" },
  { key: "age", label: "Age", type: "number" },
  { key: "niqs_membership_no", label: "NIQS membership number" },
  { key: "qsrbn_reg_no", label: "Q.S.R.B.N. registration number" },
  { key: "qsrbn_reg_date", label: "Q.S.R.B.N. registration date", type: "date" },
  { key: "academic_qualification", label: "Academic qualifications", wide: true },
  { key: "professional_qualification", label: "Professional qualifications", wide: true },
  { key: "post_qualification_experience", label: "Post-qualification experience", wide: true },
];

/* The first person listed is the applicant, who is the principal by definition;
   everyone after them defaults to partner. */
const emptyPerson = (role: string) =>
  ({ role, ...Object.fromEntries(PERSON_FIELDS.map((f) => [f.key, ""])) }) as any;

/** "Principal Partner", or "Partner 2" when the role repeats. */
function personHeading(people: any[], i: number): string {
  const role = people[i]?.role ?? "partner";
  const sameRole = people.filter((p) => p?.role === role);
  if (sameRole.length < 2) return roleLabel(role);
  const nth = people.slice(0, i + 1).filter((p) => p?.role === role).length;
  return `${roleLabel(role)} ${nth}`;
}

const safeMsg = (err: any, fallback = "Something went wrong. Please try again."): string =>
  describeApiError(err, fallback);

function StepPill({ n, active, allowed, onClick }: { n: number; active: boolean; allowed: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} disabled={!allowed}
      className={[
        "h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-all",
        active ? "bg-gray-900 text-white ring-2 ring-gray-900 ring-offset-2" : "",
        !active && allowed ? "bg-indigo-100 text-indigo-700 hover:bg-indigo-200" : "",
        !active && !allowed ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "",
      ].join(" ")}>
      {n}
    </button>
  );
}

/* String-array editor used by the affiliations list. */
function ListEditor({ label, values, onChange, placeholder }: {
  label: string; values: string[]; onChange: (v: string[]) => void; placeholder: string;
}) {
  return (
    <div>
      <label className={lbl}>{label}</label>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input className={inp} value={v} placeholder={placeholder}
              onChange={(e) => onChange(values.map((x, idx) => (idx === i ? e.target.value : x)))} />
            <button type="button" onClick={() => onChange(values.filter((_, idx) => idx !== i))}
              className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => onChange([...values, ""])}
        className="mt-2 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
        <Plus className="w-4 h-4" /> Add
      </button>
    </div>
  );
}

export default function FirmApplicationWizard() {
  const { data: elig, isLoading: eligLoading } = useGetFirmEligibilityQuery();
  const {
    data: current,
    isLoading: curLoading,
    isFetching: curFetching,
    refetch,
  } = useGetFirmCurrentQuery();

  const [step1] = useFirmStep1Mutation();
  const [step5] = useFirmStep5Mutation();
  const [step6] = useFirmStep6Mutation();
  const [step7] = useFirmStep7Mutation();
  const [acknowledge] = useFirmAcknowledgeMutation();
  const [initiatePayment, { isLoading: paying }] = useInitiateFirmPaymentMutation();
  const [uploadDoc] = useUploadFirmDocumentMutation();

  /* ── Did the server actually answer? ────────────────────────────────────
     Three states, not two. /firm/applications/current answers a member with a
     draft with the draft itself, and a member without one with a prefill stub
     carrying `id: null` — so the `id` KEY is present either way, and its
     presence is what says the server answered the question at all.

     This distinction is the whole point. Reading `current?.id` alone made a
     failed load — a 500, a timeout, or one of this API's HTTP-200-with-an-
     error-body replies — look exactly like "you have no application yet": a
     pristine step 1 with every field blank and nothing on screen to say the
     saved application had not been loaded. People were retyping forms they had
     already filled in. */
  const answered = !!current && typeof current === "object" && "id" in current;

  // Deliberately NOT `isError`: RTK keeps the last good payload through a
  // failed refetch, and one flaky refetch mid-wizard must not replace a form
  // somebody is filling in with an error card. Having no usable answer is the
  // only thing that counts as a failed load — which covers both a dead request
  // (no data at all) and a 200 carrying an error body (data, but no `id` key).
  const loadFailed = !curLoading && !answered;

  /* The chapter is shown, never chosen: whatever the draft already recorded,
     otherwise the applicant's own chapter from the server's prefill. */
  const chapterInfo: any = { ...((current as any)?.chapter_prefill ?? {}) };
  for (const k of Object.keys(chapterInfo)) {
    if ((current as any)?.[k]) chapterInfo[k] = (current as any)[k];
  }
  const chapterName = (current as any)?.chapter?.name ?? chapterInfo.state_chapter ?? null;

  const draft = answered && current.id ? current : null;
  const draftId = draft?.id;
  // A draft saved under the old numbering resumes wherever STEPS says its
  // completion_step lands, which for anything below 4 is step 1 — correct, since
  // it predates the roster step 1 now asks for.
  const serverStep = draft?.completion_step ?? 0;
  const serverNextStep = resumeStep(serverStep);

  const [step, setStep] = useState<number | null>(null);
  const activeStep = step ?? serverNextStep;

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const canGoTo = (n: number) => n >= 1 && n <= Math.max(serverNextStep, activeStep);
  const goTo = (n: number) => { if (canGoTo(n)) { setStep(n); setError(null); } };
  const jumpNext = async (msg?: string) => {
    await refetch();
    if (msg) { setFlash(msg); setTimeout(() => setFlash(null), 1200); }
    setStep((s) => Math.min((s ?? serverNextStep) + 1, TOTAL_STEPS));
  };

  const run = async (fn: () => Promise<any>, msg?: string) => {
    setBusy(true); setError(null);
    try { await fn(); await jumpNext(msg); }
    catch (e: any) { setError(safeMsg(e)); }
    finally { setBusy(false); }
  };

  /* ── local form state, seeded from the draft ── */
  const [details, setDetails] = useState<any>({});
  // Step-1 registration documents, keyed by doc_type.
  const [regDocs, setRegDocs] = useState<Record<string, any>>({});
  const [people, setPeople] = useState<any[]>([]);
  const [benevolence, setBenevolence] = useState<any[]>([]);
  const [referees, setReferees] = useState<any[]>([]);
  const [seeded, setSeeded] = useState(false);
  const [refPicking, setRefPicking] = useState(false);

  /* Seed the form from whatever the server answered with — ONCE.
   *
   * Seeded from `current`, not from `draft`. Both shapes carry the same
   * top-level keys: a draft carries what has been filled in, and the no-draft
   * stub carries the applicant's own email, phone and state. Gating this on
   * `draft` threw that stub away, so a first-time applicant was handed a blank
   * step 1 the server had already filled in for them.
   *
   * An effect rather than a `useMemo`. React only promises to run a memo
   * callback when its dependencies change — it is free to re-run or discard
   * one — so seven setState calls inside a memo were a silent blank form
   * waiting to happen the next time this tree is memoised or run under
   * StrictMode. Seeding is a side effect; it belongs in an effect.
   *
   * Once only: `seeded` latches so a refetch after each step cannot overwrite
   * what the applicant is in the middle of typing.
   */
  useEffect(() => {
    if (!answered || seeded) return;

    const src: any = current;

    setDetails({
      practice_name: src.practice_name ?? "", nature_of_practice: src.nature_of_practice ?? "",
      registered_address: src.registered_address ?? "",
      email: src.email ?? "", phone: src.phone ?? "",
      cama_reg_number: src.cama_reg_number ?? "",
      cac_reg_date: src.cac_reg_date?.slice(0, 10) ?? "", commencement_date: src.commencement_date?.slice(0, 10) ?? "",
      ownership_structure: src.ownership_structure ?? "", num_partners: src.num_partners ?? 1,
      affiliations: src.affiliations ?? [],
      // No longer asked for — carried straight back so the firm keeps a state.
      state_id: src.state_id ?? "",
    });
    const allDocs: any[] = src.documents ?? [];
    setRegDocs(Object.fromEntries(
      allDocs.filter((d) => REG_DOC_TYPES.includes(d.doc_type))
        .map((d) => [d.doc_type, { file_name: d.file_name, file_path: d.file_path }]),
    ));
    /* The roster. A fresh application starts with one row — the applicant, who
       is the principal by definition — prefilled from their upgrade application
       (current.principal_prefill). */
    const saved: any[] = src.partners ?? [];
    const pp = src.principal_prefill ?? {};
    setPeople(saved.length
      ? saved.map((p: any) => ({
          role: p.role ?? "partner",
          ...Object.fromEntries(PERSON_FIELDS.map((f) => [f.key, p[f.key] ?? ""])),
          qsrbn_reg_date: p.qsrbn_reg_date?.slice(0, 10) ?? "",
        }))
      : [{
          ...emptyPerson("principal"),
          full_name: pp.full_name ?? "",
          niqs_membership_no: pp.niqs_membership_no ?? "",
          academic_qualification: pp.academic_qualification ?? "",
          professional_qualification: pp.professional_qualification ?? "",
          post_qualification_experience: pp.post_qualification_experience ?? "",
        }]);
    setBenevolence((src.benevolence_records ?? []).map((b: any) => ({
      type: b.type ?? "", description: b.description ?? "",
    })));
    setReferees((src.referees ?? []).map((r: any) => ({
      member_id: r.member_id,
      _label: r.member ? [r.member.surname, r.member.firstname].filter(Boolean).join(" ") : r.member_id,
    })));
    setSeeded(true);
  }, [answered, seeded, current]);

  /* ── step handlers ── */
  const isSole = details.ownership_structure === SOLE_PROPRIETORSHIP;
  // A sole proprietorship is the principal on their own; anything else declares
  // however many partners/directors the applicant says it has. No structure
  // chosen yet means the count question hasn't been reached.
  const asksCount = !!details.ownership_structure && !isSole;
  const peopleCount = isSole ? 1 : (asksCount ? Math.max(1, Number(details.num_partners) || 1) : 0);
  // Exactly one principal — that row is the applicant, and it is their member_id
  // the server binds the firm to.
  const principalCount = people.filter((p) => p?.role === "principal").length;

  /* Keep the roster exactly as long as the declared count, so what is on screen
     is what gets saved — blocks rendered from a bare `Array.from` looked filled
     in but were never in state, and saving dropped them. Guarded on a count of
     zero: before a structure is chosen there is no count yet, and wiping the
     roster there would throw away the applicant's prefilled principal row. */
  useEffect(() => {
    if (!seeded || peopleCount === 0) return;
    setPeople((prev) => {
      if (prev.length === peopleCount) return prev;
      if (prev.length > peopleCount) return prev.slice(0, peopleCount);
      return prev.concat(Array.from({ length: peopleCount - prev.length },
        (_, i) => emptyPerson(prev.length + i === 0 ? "principal" : "partner")));
    });
  }, [peopleCount, seeded]);

  const saveStep1 = () => run(async () => {
    const res = await step1({
      ...details,
      affiliations: (details.affiliations ?? []).filter((a: string) => a.trim()),
      documents: REG_DOCS
        .map(({ type }) => ({ doc_type: type, ...(regDocs[type] ?? {}) }))
        .filter((d) => d.file_path),
      partners: people,
    }).unwrap();
    if (!draftId && res?.data?.id) { /* draft created; refetch will pick it up */ }
  }, "Firm details saved.");

  /* Registration documents upload as soon as they're chosen (to the shared
     document store, which doesn't need the draft to exist yet), so they can be
     viewed straight away; saving step 1 attaches the returned paths. */
  const [uploadingRegDoc, setUploadingRegDoc] = useState<string | null>(null);
  const uploadRegDoc = async (docType: string, file: File) => {
    setError(null);
    setUploadingRegDoc(docType);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const res = await uploadDoc(fd).unwrap();
      setRegDocs((prev) => ({
        ...prev,
        [docType]: { file_name: file.name, file_path: res.path, file_url: (res as any).url ?? firmStorageUrl(res.path) },
      }));
    } catch (e: any) {
      setError(safeMsg(e, "Upload failed. Please try again."));
    } finally {
      setUploadingRegDoc(null);
    }
  };

  // Rows with neither a type nor a description are simply blanks the applicant
  // added and left — drop them rather than failing the save.
  const benevolenceRows = benevolence.filter((b) => b.type || b.description.trim());
  const saveStep5 = () => run(
    () => step5({
      id: draftId,
      benevolence_records: benevolenceRows.map((b) => ({ type: b.type, description: b.description.trim() })),
    }).unwrap(),
    "Benevolence saved.");

  const saveStep6 = () => {
    if (referees.length !== 2) { setError("Please select exactly two referees."); return; }
    run(() => step6({ id: draftId, referees: referees.map((r) => ({ member_id: r.member_id })) }).unwrap(), "Referees saved.");
  };

  // Nothing to submit — the server stamps the applicant's own chapter.
  const saveStep7 = () => run(() => step7({ id: draftId }).unwrap(), "Chapter recorded.");

  /* The declaration and the payment are one screen, so they are one action:
     record the acknowledgement, then charge. Acknowledging is idempotent, so a
     failed payment can simply be retried. */
  const [declared, setDeclared] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const acknowledgeAndPay = async () => {
    setError(null);
    setAcknowledging(true);
    try {
      await acknowledge({ id: draftId }).unwrap();
      const res = await initiatePayment().unwrap();
      redirectToOnePay(res);
    } catch (e: any) {
      const blockers = e?.data?.blockers;
      setError(Array.isArray(blockers) ? blockers.join(" ") : safeMsg(e, "Could not start payment."));
    } finally {
      setAcknowledging(false);
    }
  };

  if (eligLoading || curLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  /* The load failed. Say so and offer to try again — NEVER fall through to a
     blank step 1, which is indistinguishable from a new application and has
     people retyping a form they already filled in. Eligibility answers from the
     same `currentDraft()` query, so where it names a draft we can say plainly
     that one exists and was not loaded. */
  if (loadFailed) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="text-lg font-bold text-red-900 mb-1">We couldn't load your application</h2>
              <p className="text-sm text-red-800">
                {elig?.draft_id
                  ? `Your saved application (#${String(elig.draft_id).padStart(6, "0")}) is still there — we just couldn't fetch it. Nothing has been lost.`
                  : "Nothing has been lost. Please try again in a moment."}
              </p>
              <div className="flex items-center gap-4 mt-4">
                <button
                  onClick={() => refetch()}
                  disabled={curFetching}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60"
                >
                  <RefreshCw className={`w-4 h-4 ${curFetching ? "animate-spin" : ""}`} />
                  {curFetching ? "Retrying…" : "Try again"}
                </button>
                <Link to="/firm-applications" className="text-sm text-indigo-600 hover:underline">← Back to applications</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Not eligible and no draft yet → explain why.
  if (elig && !elig.can_apply && !draft) {
    return (
      <div className="max-w-lg mx-auto p-6">
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
          <h2 className="text-lg font-bold text-amber-900 mb-2">You can't register a firm right now</h2>
          <ul className="list-disc ml-5 text-sm text-amber-800 space-y-1">
            {(elig.reasons ?? []).map((r: string, i: number) => <li key={i}>{r}</li>)}
          </ul>
          <Link to="/firm-applications" className="inline-block mt-4 text-sm text-indigo-600 hover:underline">← Back</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 md:p-6">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Firm Application</h1>
          <p className="text-sm text-gray-500 mt-0.5">Step {activeStep} of {TOTAL_STEPS} — {STEPS[activeStep - 1].label}</p>
        </div>
        <Link to="/firm-applications" className="text-sm text-indigo-600 hover:underline shrink-0">← Back to applications</Link>
      </div>

      <div className="flex items-center gap-1.5 mb-4 flex-wrap">
        {STEPS.map((_, i) => (
          <StepPill key={i} n={i + 1} active={activeStep === i + 1} allowed={canGoTo(i + 1)} onClick={() => goTo(i + 1)} />
        ))}
      </div>

      {flash && <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">{flash}</div>}
      {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

      <div className="bg-white border rounded-xl p-5 md:p-6 shadow-sm">
        {/* STEP 1 — details */}
        {activeStep === 1 && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Firm Details</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2"><label className={lbl}>Practice name *</label>
                <input className={inp} value={details.practice_name ?? ""} onChange={(e) => setDetails({ ...details, practice_name: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={lbl}>General nature of practice</label>
                <input className={inp} value={details.nature_of_practice ?? ""} onChange={(e) => setDetails({ ...details, nature_of_practice: e.target.value })} /></div>
              <div className="sm:col-span-2"><label className={lbl}>Registered address</label>
                <textarea className={inp} rows={2} value={details.registered_address ?? ""} onChange={(e) => setDetails({ ...details, registered_address: e.target.value })} /></div>
              <div><label className={lbl}>Firm email</label>
                <input type="email" className={inp} value={details.email ?? ""} onChange={(e) => setDetails({ ...details, email: e.target.value })} /></div>
              <div><label className={lbl}>Firm phone number</label>
                <input className={inp} value={details.phone ?? ""} onChange={(e) => setDetails({ ...details, phone: e.target.value })} /></div>
              <div className="sm:col-span-2 pt-2">
                <h3 className="text-sm font-bold text-gray-900">
                  Particulars of registration under the Companies and Allied Matters Act
                  or the Registration of Business Name Act
                </h3>
              </div>
              <div><label className={lbl}>CAC registration number</label>
                <input className={inp} value={details.cama_reg_number ?? ""} onChange={(e) => setDetails({ ...details, cama_reg_number: e.target.value })} /></div>
              <div><label className={lbl}>Date of registration with Corporate Affairs Commission</label>
                <input type="date" className={inp} value={details.cac_reg_date ?? ""} onChange={(e) => setDetails({ ...details, cac_reg_date: e.target.value })} /></div>
              <div><label className={lbl}>Date of commencement of practice</label>
                <input type="date" className={inp} value={details.commencement_date ?? ""} onChange={(e) => setDetails({ ...details, commencement_date: e.target.value })} /></div>
              <div />

              {REG_DOCS.map(({ type, label }) => {
                const d = regDocs[type];
                const url = d?.file_url ?? firmStorageUrl(d?.file_path);
                return (
                  <div key={type} className="sm:col-span-2">
                    <label className={lbl}>{label} (PDF)</label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <input type="file" accept=".pdf" className="text-sm"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadRegDoc(type, f); }} />
                      {uploadingRegDoc === type && <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />}
                      {uploadingRegDoc !== type && url && (
                        <>
                          <span className="text-xs text-gray-400">Uploaded: {d.file_name}</span>
                          <a href={url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 hover:underline">View</a>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}

              <div className="sm:col-span-2 border-t pt-4">
                <h3 className="text-sm font-bold text-gray-900">Ownership structure</h3>
              </div>
              <div><label className={lbl}>How is the practice owned? *</label>
                <select className={inp} value={details.ownership_structure ?? ""}
                  onChange={(e) => setDetails({ ...details, ownership_structure: e.target.value })}>
                  <option value="">Select…</option>
                  {OWNERSHIP_GROUPS.map(({ group, options }) => (
                    group
                      ? <optgroup key={group} label={group}>
                          {options.map((o) => <option key={o} value={o}>{o}</option>)}
                        </optgroup>
                      : options.map((o) => <option key={o} value={o}>{o}</option>)
                  ))}
                </select></div>
              {/* A sole proprietorship is the principal on their own. */}
              {asksCount && (
                <div><label className={lbl}>Number of partners / directors</label>
                  <input type="number" min={1} max={100} className={inp} value={details.num_partners ?? 1}
                    onChange={(e) => setDetails({ ...details, num_partners: e.target.value })} /></div>
              )}
            </div>

            {/* One C.V. block per person, headed by whichever role they hold. */}
            {peopleCount > 0 && (
              <div className="mt-5 space-y-4">
                {people.map((person, i) => {
                  const setField = (key: string, value: string) =>
                    setPeople(people.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
                  return (
                    <div key={i} className="border rounded-xl p-4 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-900 mb-3">{personHeading(people, i)}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2"><label className={lbl}>Role *</label>
                          <select className={inp} value={person.role ?? ""}
                            onChange={(e) => setField("role", e.target.value)}>
                            {PERSON_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                          </select></div>
                        {PERSON_FIELDS.map(({ key, label, type, wide }) => (
                          <div key={key} className={wide ? "sm:col-span-2" : undefined}>
                            <label className={lbl}>{label}</label>
                            <input type={type ?? "text"} className={inp} value={person[key] ?? ""}
                              onChange={(e) => setField(key, e.target.value)} />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
                {principalCount !== 1 && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    {principalCount === 0
                      ? "One person must be listed as the Principal Partner — that is you, the applicant."
                      : "Only one person may be listed as the Principal Partner."}
                  </p>
                )}
              </div>
            )}

            <div className="mt-5 border-t pt-4">
              <ListEditor label="Affiliations with other firms in Nigeria or abroad" placeholder="Affiliated firm"
                values={details.affiliations ?? []} onChange={(v) => setDetails({ ...details, affiliations: v })} />
            </div>

            <div className="flex justify-end mt-6">
              <button onClick={saveStep1}
                disabled={busy || !!uploadingRegDoc || !details.practice_name
                  || !details.ownership_structure || principalCount !== 1}
                className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60">
                {busy ? "Saving…" : "Save & continue"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 2 — benevolence */}
        {activeStep === 2 && draftId && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Acts of Benevolence to NIQS</h2>
            <p className="text-sm text-gray-500 mb-4">
              Record each contribution made since your election as a member — its type, and what was done.
              This step is optional: leave it empty to skip.
            </p>
            <div className="space-y-3">
              {benevolence.map((b, i) => (
                <div key={i} className="border rounded-xl p-4 bg-gray-50">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><label className={lbl}>Type of benevolence</label>
                        <select className={inp} value={b.type}
                          onChange={(e) => setBenevolence(benevolence.map((x, idx) => idx === i ? { ...x, type: e.target.value } : x))}>
                          <option value="">Select type…</option>
                          {BENEVOLENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select></div>
                      <div className="sm:col-span-2"><label className={lbl}>What was done</label>
                        <textarea className={inp} rows={3} placeholder="Describe the act of benevolence…"
                          value={b.description}
                          onChange={(e) => setBenevolence(benevolence.map((x, idx) => idx === i ? { ...x, description: e.target.value } : x))} /></div>
                    </div>
                    <button type="button" onClick={() => setBenevolence(benevolence.filter((_, idx) => idx !== i))}
                      className="p-2 text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setBenevolence([...benevolence, { type: "", description: "" }])}
              className="mt-3 inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
              <Plus className="w-4 h-4" /> Add an act of benevolence
            </button>
            {benevolenceRows.some((b) => !b.type || !b.description.trim()) && (
              <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                Every act needs both a type and a description.
              </p>
            )}
            <div className="flex justify-between mt-6">
              <button onClick={() => goTo(1)} className="px-5 py-2.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">Back</button>
              <button onClick={saveStep5}
                disabled={busy || benevolenceRows.some((b) => !b.type || !b.description.trim())}
                className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60">
                {busy ? "Saving…" : "Save & continue"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — referees */}
        {activeStep === 3 && draftId && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Referees</h2>
            <p className="text-sm text-gray-500 mb-4">Two corporate members (Member or Fellow) who will vouch for the firm.</p>
            <div className="space-y-2">
              {referees.map((r, i) => (
                <div key={r.member_id} className="flex items-center gap-3 bg-white border rounded-xl px-4 py-3">
                  <div className="w-9 h-9 rounded-lg bg-indigo-600 flex items-center justify-center text-white font-bold">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">{r._label ?? r.member_id}</p>
                    <span className="flex items-center gap-1 text-xs text-gray-400"><Fingerprint className="w-3 h-3" /> {r.member_id}</span>
                  </div>
                  <button type="button" onClick={() => setReferees(referees.filter((_, idx) => idx !== i))}
                    className="p-1.5 text-gray-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {referees.length < 2 && (
              <button type="button" onClick={() => setRefPicking(true)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700">
                <UserPlus className="w-4 h-4" /> Add referee
              </button>
            )}
            <div className="flex justify-between mt-6">
              <button onClick={() => goTo(2)} className="px-5 py-2.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">Back</button>
              <button onClick={saveStep6} disabled={busy || referees.length !== 2}
                className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60">
                {busy ? "Saving…" : "Save & continue"}
              </button>
            </div>
            {refPicking && (
              <MemberSearchModal title="Add a referee" enforceEligible={false}
                existing={referees.map((r) => r.member_id)}
                onPick={(m) => setReferees([...referees, { member_id: m.member_id, _label: [m.surname, m.firstname].filter(Boolean).join(" ") + ` (${m.membership_no ?? ""})` }])}
                onClose={() => setRefPicking(false)} />
            )}
          </div>
        )}

        {/* STEP 4 — chapter. Read-only: the applicant's own chapter, and who to
            approach for endorsement once the fee is paid. */}
        {activeStep === 4 && draftId && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Your Chapter</h2>
            <p className="text-sm text-gray-500 mb-4">
              This is the chapter on your membership record. It endorses your application — you don't
              choose it here.
            </p>

            {chapterName ? (
              <div className="border rounded-xl p-4 bg-gray-50">
                <p className="text-base font-semibold text-gray-900">{chapterName}</p>
                <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {[
                    ["Chairman", chapterInfo.chapter_chairman_name, chapterInfo.chapter_chairman_email, chapterInfo.chapter_chairman_phone],
                    ["Secretary", chapterInfo.chapter_secretary_name, chapterInfo.chapter_secretary_email, chapterInfo.chapter_secretary_phone],
                  ].map(([role, name, email, phone]) => name ? (
                    <div key={role as string}>
                      <dt className="text-xs text-gray-400">{role}</dt>
                      <dd className="text-gray-900">{name}</dd>
                      {email && <dd className="text-xs text-gray-500">{email}</dd>}
                      {phone && <dd className="text-xs text-gray-500">{phone}</dd>}
                    </div>
                  ) : null)}
                </dl>
              </div>
            ) : (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                Your membership record has no chapter, so your application cannot be endorsed.
                Please contact the secretariat to have your chapter set before you continue.
              </div>
            )}

            <div className="mt-4 p-3 rounded-lg bg-indigo-50 border border-indigo-200 text-sm text-indigo-900">
              <span className="font-semibold">After you pay</span>, reach out to your chapter and ask
              them to endorse this application. Your chapter chairman will see it on the chapter portal.
              The secretariat can only review it once the chapter has endorsed.
            </div>

            <div className="flex justify-between mt-6">
              <button onClick={() => goTo(3)} className="px-5 py-2.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">Back</button>
              <button onClick={saveStep7} disabled={busy || !chapterName}
                className="px-5 py-2.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-60">
                {busy ? "Saving…" : "Continue"}
              </button>
            </div>
          </div>
        )}

        {/* STEP 5 — declaration & payment, one screen */}
        {activeStep === 5 && draftId && (
          <div>
            <h2 className="text-lg font-bold text-gray-900 mb-4">Declaration &amp; Payment</h2>
            <p className="text-sm text-gray-600 mb-4">
              I pledge to conduct the practice strictly in compliance with the rules of conduct and to abide
              by the constitution and code of ethics and professional conduct of the Nigerian Institute of
              Quantity Surveyors, as may be amended thereafter.
            </p>
            <label className="flex items-start gap-2 text-sm text-gray-700 mb-6">
              <input type="checkbox" className="mt-0.5" checked={declared}
                onChange={(e) => setDeclared(e.target.checked)} />
              <span>I acknowledge the declaration above.</span>
            </label>
            <p className="text-sm text-gray-500 mb-6">
              {elig?.fee_naira != null
                ? <>The firm registration fee is <span className="font-semibold">₦{Number(elig.fee_naira).toLocaleString()}</span>.</>
                : "Proceed to pay the firm registration fee."}
            </p>
            <div className="flex justify-between">
              <button onClick={() => goTo(4)} className="px-5 py-2.5 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">Back</button>
              <button onClick={acknowledgeAndPay} disabled={!declared || acknowledging || paying}
                className="px-6 py-2.5 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60">
                {acknowledging || paying ? "Starting payment…" : "Pay & submit"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
